import { Link, Toast } from 'types/layout';
import VueRouter, { Route } from 'vue-router';
import axios from 'axios';
import { correlationIdHeader } from './interceptors/correlation-id-interceptor-request';
import { errorHandler } from './interceptors/error-handler-interceptor';
import { investmentEntity } from '@store/modules/investment-entity';
import { oAuthTokenHeader } from './interceptors/oauth-interceptor-request';
import { oAuthTokenRefresh } from './interceptors/oauth-interceptor-response';
import { orderForm } from '@store/modules/order-form';
import { passwordResetKeyHeader } from './interceptors/password-reset-key-interceptor-request';
import { passwordResetSessionUuidHeader } from './interceptors/password-reset-session-uuid-interceptor-request';

export const apiSecureRequest = axios.create({
	baseURL: `${process.env.VUE_APP_BASE_API_URL}/api-secured`
});

declare module 'axios' {
	export interface AxiosRequestConfig {
		errorToast?: Toast;
		suppressToast?: (response: AxiosResponse) => boolean;
	}
}

apiSecureRequest.interceptors.request.use(
	(config) => oAuthTokenHeader(config),
	(error) => Promise.reject(error)
);

apiSecureRequest.interceptors.request.use(
	(config) => correlationIdHeader(config),
	(error) => Promise.reject(error)
);

apiSecureRequest.interceptors.response.use(
	(response) => {
		return response;
	},
	(error) => oAuthTokenRefresh(error)
);

apiSecureRequest.interceptors.response.use(
	(response) => {
		return response;
	},
	(error) => errorHandler(error)
);

export const forgotPasswordApiRequest = axios.create({
	baseURL: `${process.env.VUE_APP_BASE_API_URL}/api`
});

forgotPasswordApiRequest.interceptors.request.use(
	(config) => passwordResetKeyHeader(config),
	(error) => Promise.reject(error)
);

forgotPasswordApiRequest.interceptors.request.use(
	(config) => passwordResetSessionUuidHeader(config),
	(error) => Promise.reject(error)
);

forgotPasswordApiRequest.interceptors.request.use(
	(config) => correlationIdHeader(config),
	(error) => Promise.reject(error)
);

forgotPasswordApiRequest.interceptors.response.use(
	(response) => {
		return response;
	},
	(error) => errorHandler(error)
);

export const investmentEntityRequest = axios.create();

investmentEntityRequest.interceptors.request.use(
	(config) => {
		config.baseURL = `${process.env.VUE_APP_BASE_API_URL}/api-secured/investment-entity/${investmentEntity.investmentEntityId}`;
		return config;
	},
	(error) => Promise.reject(error)
);

investmentEntityRequest.interceptors.request.use(
	(config) => oAuthTokenHeader(config),
	(error) => Promise.reject(error)
);

investmentEntityRequest.interceptors.request.use(
	(config) => correlationIdHeader(config),
	(error) => Promise.reject(error)
);

investmentEntityRequest.interceptors.response.use(
	(response) => {
		return response;
	},
	(error) => oAuthTokenRefresh(error)
);

investmentEntityRequest.interceptors.response.use(
	(response) => {
		return response;
	},
	(error) => errorHandler(error)
);

export const investmentEntityApsRequest = axios.create();

investmentEntityApsRequest.interceptors.request.use(
	(config) => {
		config.baseURL = `${process.env.VUE_APP_BASE_API_URL}/api-secured/aps/investment-entity/${investmentEntity.investmentEntityId}`;
		return config;
	},
	(error) => Promise.reject(error)
);

investmentEntityApsRequest.interceptors.request.use(
	(config) => oAuthTokenHeader(config),
	(error) => Promise.reject(error)
);

investmentEntityApsRequest.interceptors.request.use(
	(config) => correlationIdHeader(config),
	(error) => Promise.reject(error)
);

investmentEntityApsRequest.interceptors.response.use(
	(response) => {
		return response;
	},
	(error) => oAuthTokenRefresh(error)
);

investmentEntityApsRequest.interceptors.response.use(
	(response) => {
		return response;
	},
	(error) => errorHandler(error)
);

export const oauthRequest = axios.create({
	baseURL: `${process.env.VUE_APP_BASE_OAUTH_API_URL}`
});

oauthRequest.interceptors.response.use(
	(response) => {
		return response;
	},
	(error) => errorHandler(error)
);

export const orderGroupInvestmentRequest = axios.create();

orderGroupInvestmentRequest.interceptors.request.use(
	(config) => {
		config.baseURL = `${process.env.VUE_APP_BASE_API_URL}/api-secured/investment-entity/${investmentEntity.investmentEntityId}/investment/order-group/${orderForm.orderGroupId}`;
		return config;
	},
	(error) => Promise.reject(error)
);

orderGroupInvestmentRequest.interceptors.request.use(
	(config) => oAuthTokenHeader(config),
	(error) => Promise.reject(error)
);

orderGroupInvestmentRequest.interceptors.request.use(
	(config) => correlationIdHeader(config),
	(error) => Promise.reject(error)
);

orderGroupInvestmentRequest.interceptors.response.use(
	(response) => {
		return response;
	},
	(error) => oAuthTokenRefresh(error)
);

orderGroupInvestmentRequest.interceptors.response.use(
	(response) => {
		return response;
	},
	(error) => errorHandler(error)
);

export const apiRequest = axios.create({
	baseURL: `${process.env.VUE_APP_BASE_API_URL}/api`
});

apiRequest.interceptors.response.use(
	(response) => {
		return response;
	},
	(error) => errorHandler(error)
);

export const webRequest = axios.create({
	baseURL: `${process.env.VUE_APP_BASE_URL}`
});

export const staticRequest = axios.create({
	baseURL: `${process.env.VUE_APP_CLOUDFRONT_DOMAIN}`
});

export const constructUrlWithQueryParams = (url: string, params: object): string => {
	const initialSeparator = url.indexOf('?') === -1 ? '?' : '&';
	const entries = Object.entries(params);
	const paramArray: Array<string> = [];

	for (const [key, val] of entries) {
		paramArray.push(`${key}=${val}`);
	}

	return `${url + initialSeparator + paramArray.join('&')}`;
};

export const flattenQueryParam = (queryParam: string | (string | null)[]): string | null => {
	if (queryParam === null) {
		return null;
	}

	return Array.isArray(queryParam) ? queryParam[0] : queryParam;
};

export const includesRequiredQueryParams = (requiredParams: Array<string>, to: Route): boolean => {
	return requiredParams.every((param) => Object.keys(to.query).includes(param));
};

export function getLinkFromRoute(router: VueRouter, routeNameOrPath: string, linkText: string): Link {
	const routeNameResolution = router.resolve({ name: routeNameOrPath });
	const routePathResolution = router.resolve(routeNameOrPath);
	if (routeNameResolution.resolved.matched.length > 0 && routeNameResolution.resolved.name) {
		return { router: routeNameResolution.resolved.name, text: linkText };
	} else if (routePathResolution.resolved.matched.length > 0 && routePathResolution.resolved.name) {
		return {
			router: routePathResolution.resolved.name,
			text: linkText,
			params: routePathResolution.resolved.params
		};
	} else {
		const startOfPathOptions = ['https://', 'http://', 'mailto:', '/'];
		if (startOfPathOptions.some((option) => routeNameOrPath.startsWith(option))) {
			return { href: routeNameOrPath, text: linkText };
		} else {
			return { href: `/${routeNameOrPath}`, text: linkText };
		}
	}
}
